import React from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useLeadNotifications } from '../components/notifications/ToastProvider';
import { ExternalLink, Plus, User, FileText, AlertTriangle } from 'lucide-react';

const ToastDemo = () => {
  const notify = useNotifications();
  const leadNotify = useLeadNotifications();

  const demoActions = [
    {
      label: 'Voir les détails',
      onClick: () => console.log('Navigation vers les détails'),
      variant: 'primary',
      icon: <ExternalLink size={14} />
    },
    {
      label: 'Créer une tâche',
      onClick: () => console.log('Ouverture du modal de tâche'),
      variant: 'secondary',
      icon: <Plus size={14} />
    }
  ];

  return (
    <div style={{
      padding: '40px',
      maxWidth: '600px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <h1 style={{ 
        color: '#1e293b', 
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        🎯 Démonstration du Système de Toast
      </h1>
      
      <div style={{
        display: 'grid',
        gap: '16px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
      }}>
        
        {/* Notifications de base */}
        <div style={{
          background: '#f8fafc',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ color: '#374151', marginBottom: '16px', fontSize: '16px' }}>
            ✨ Notifications de base
          </h3>
          
          <button 
            onClick={() => notify.success('Opération réussie !', 'Votre action a été effectuée avec succès.')}
            style={buttonStyle('#10b981')}
          >
            <User size={16} />
            Succès
          </button>
          
          <button 
            onClick={() => notify.error('Erreur détectée', 'Une erreur est survenue lors du traitement.')}
            style={buttonStyle('#ef4444')}
          >
            <AlertTriangle size={16} />
            Erreur
          </button>
          
          <button 
            onClick={() => notify.info('Information', 'Voici une information importante à noter.')}
            style={buttonStyle('#3b82f6')}
          >
            <FileText size={16} />
            Info
          </button>
          
          <button 
            onClick={() => notify.warning('Attention', 'Cette action nécessite votre attention.')}
            style={buttonStyle('#f59e0b')}
          >
            <AlertTriangle size={16} />
            Attention
          </button>
        </div>

        {/* Notifications CRM spécialisées */}
        <div style={{
          background: '#f8fafc',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ color: '#374151', marginBottom: '16px', fontSize: '16px' }}>
            🏢 Notifications CRM
          </h3>
          
          <button 
            onClick={() => notify.clientSaved('Jean Dupont', 'CLT-2024-001')}
            style={buttonStyle('#10b981')}
          >
            Client enregistré
          </button>
          
          <button 
            onClick={() => notify.documentUploaded('Passeport')}
            style={buttonStyle('#3b82f6')}
          >
            Document ajouté
          </button>
          
          <button 
            onClick={() => notify.authError()}
            style={buttonStyle('#ef4444')}
          >
            Erreur d'auth
          </button>
          
          <button 
            onClick={() => notify.serverError()}
            style={buttonStyle('#ef4444')}
          >
            Erreur serveur
          </button>
        </div>

        {/* Notifications avec actions */}
        <div style={{
          background: '#f8fafc',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ color: '#374151', marginBottom: '16px', fontSize: '16px' }}>
            🚀 Avec actions
          </h3>
          
          <button 
            onClick={() => notify.success(
              'Traitement terminé !', 
              'Votre demande a été traitée avec succès.',
              demoActions
            )}
            style={buttonStyle('#10b981')}
          >
            Succès + Actions
          </button>
          
          <button 
            onClick={() => leadNotify.notifyLeadCreated(
              'LEAD-001',
              'Marie Martin',
              (id) => console.log(`Navigation vers lead ${id}`),
              (id) => console.log(`Création de tâche pour lead ${id}`)
            )}
            style={buttonStyle('#8b5cf6')}
          >
            Lead créé
          </button>
          
          <button 
            onClick={() => leadNotify.notifyLeadError('Connexion impossible au serveur')}
            style={buttonStyle('#ef4444')}
          >
            Erreur lead
          </button>
        </div>

      </div>

      {/* Instructions */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        background: '#fefce8',
        border: '1px solid #fbbf24',
        borderRadius: '12px'
      }}>
        <h4 style={{ color: '#92400e', marginBottom: '12px' }}>
          📋 Instructions d'utilisation
        </h4>
        <ul style={{ color: '#92400e', lineHeight: '1.6' }}>
          <li>Cliquez sur les boutons pour tester les différents types de notifications</li>
          <li>Les toasts apparaissent en haut à droite de l'écran</li>
          <li>Ils se ferment automatiquement après quelques secondes</li>
          <li>Vous pouvez les fermer manuellement avec le bouton ×</li>
          <li>Les notifications avec actions permettent des interactions supplémentaires</li>
        </ul>
      </div>

      {/* Code d'exemple */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        background: '#1e293b',
        borderRadius: '12px',
        color: '#e2e8f0',
        fontSize: '14px',
        overflow: 'auto'
      }}>
        <h4 style={{ color: '#f8fafc', marginBottom: '12px' }}>
          💻 Exemple de code
        </h4>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{`import { useNotifications } from '../hooks/useNotifications';

function MonComposant() {
  const notify = useNotifications();
  
  const handleSave = () => {
    // Remplace: alert('Client sauvegardé !');
    notify.clientSaved('Jean Dupont', 'CLT-2024-001');
  };
  
  const handleError = () => {
    // Remplace: alert('Erreur !');
    notify.error('Erreur', 'Une erreur est survenue');
  };
  
  return <button onClick={handleSave}>Sauvegarder</button>;
}`}
        </pre>
      </div>
    </div>
  );
};

const buttonStyle = (color) => ({
  width: '100%',
  padding: '12px 16px',
  marginBottom: '8px',
  background: color,
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.2s ease',
  ':hover': {
    opacity: 0.9,
    transform: 'translateY(-1px)'
  }
});

export default ToastDemo;